import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '@/api/client';
import { API_MAIL_MESSAGES } from '@/api/endpoints';
import {
  MAIL_MESSAGE_IDS_QUERY_KEY,
  MAIL_MESSAGES_QUERY_KEY,
} from '@/api/queryKeys';
import { MailDraftType } from '@/types/components';
import type {
  MailAttachment,
  MailDraftRequest,
  MailMessageResponse,
  Recipient,
} from '@/types/mail';
import { unwrapAxiosResponse } from '@/utils/axios';
import { ErrorMessage, logRequestError } from '@/utils/errors';
import { joinPath } from '@/utils/global';
import { isBlank, stripHtml } from '@/utils/text';

const DRAFT_SAVE_DELAY_MS = 3000;

const dropDuplicateRecipients = (recipients: Recipient[]): Recipient[] => {
  const seenIds = new Set<number>();

  return recipients.filter(({ id }) => {
    if (seenIds.has(id)) {
      return false;
    }

    seenIds.add(id);

    return true;
  });
};

interface Params {
  subject: string;
  body: string;
  recipients: Recipient[];
  attachments: MailAttachment[];
  hasContent: boolean;
  messageType: MailDraftType;
  previousMessageId: number;
  draftId?: number;
}

export const useComposeDraft = ({
  subject,
  body,
  recipients,
  attachments,
  hasContent,
  messageType,
  previousMessageId,
  draftId,
}: Params) => {
  const needsPreviousMessage =
    messageType !== MailDraftType.New && draftId === undefined;

  const [quote, setQuote] = useState('');

  const queryClient = useQueryClient();

  const uniqueRecipients = dropDuplicateRecipients(recipients);

  const request: MailDraftRequest = {
    subject,
    body,
    previousMessageId,
    messageType,
    recipients: uniqueRecipients,
    attachments,
  };
  const requestKey = JSON.stringify(request);

  const requestRef = useRef(request);
  const hasContentRef = useRef(hasContent);
  const savedRequestKeyRef = useRef<string | null>(null);
  const draftIdRef = useRef<number | null>(draftId ?? null);
  const saveQueueRef = useRef(Promise.resolve());
  const timerRef = useRef<number>(undefined);
  const isPreparedRef = useRef(false);
  const isSentRef = useRef(false);

  const createDraft = useCallback(async () => {
    const response = await apiClient.post<MailMessageResponse>(
      API_MAIL_MESSAGES,
      requestRef.current,
    );
    const draft = unwrapAxiosResponse(response);

    draftIdRef.current = draft.id;

    return draft;
  }, []);

  const saveDraft = useCallback(async () => {
    if (isSentRef.current) {
      return;
    }

    const savedKey = JSON.stringify(requestRef.current);

    try {
      if (draftIdRef.current === null) {
        await createDraft();
      } else {
        await apiClient.put(
          joinPath(API_MAIL_MESSAGES, String(draftIdRef.current)),
          requestRef.current,
        );
      }

      savedRequestKeyRef.current = savedKey;
    } catch (error) {
      logRequestError(ErrorMessage.SaveMailDraft, error);
    }
  }, [createDraft]);

  const enqueueSave = useCallback(() => {
    saveQueueRef.current = saveQueueRef.current.then(saveDraft);
  }, [saveDraft]);

  const sendDraft = async () => {
    window.clearTimeout(timerRef.current);
    await saveQueueRef.current;

    const sentDraftId = draftIdRef.current ?? (await createDraft()).id;

    await apiClient.put(
      `${joinPath(API_MAIL_MESSAGES, String(sentDraftId))}/send`,
      {
        ...requestRef.current,
        recipients: uniqueRecipients.map((recipient) => ({
          id: recipient.id,
          name: recipient.name,
        })),
      },
    );

    isSentRef.current = true;
  };

  useEffect(() => {
    requestRef.current = request;
    hasContentRef.current = hasContent;
  });

  useEffect(() => {
    if (!needsPreviousMessage || isPreparedRef.current) {
      return;
    }

    isPreparedRef.current = true;

    const prepareDraft = async () => {
      try {
        const draft = await createDraft();
        const hasPreviousBody = !isBlank(stripHtml(draft.body));

        if (hasPreviousBody) {
          setQuote(draft.body);
        }
      } catch (error) {
        logRequestError(ErrorMessage.SaveMailDraft, error);
      }
    };

    saveQueueRef.current = saveQueueRef.current.then(prepareDraft);
  }, [needsPreviousMessage, createDraft]);

  useEffect(() => {
    if (!hasContent || savedRequestKeyRef.current === requestKey) {
      return;
    }

    timerRef.current = window.setTimeout(enqueueSave, DRAFT_SAVE_DELAY_MS);

    return () => window.clearTimeout(timerRef.current);
  }, [requestKey, hasContent, enqueueSave]);

  useEffect(
    () => () => {
      const hasUnsavedChanges =
        savedRequestKeyRef.current !== JSON.stringify(requestRef.current);

      if (hasContentRef.current && hasUnsavedChanges) {
        enqueueSave();
      }

      const refreshMailList = () => {
        const hasSavedDraft = draftIdRef.current !== null;

        if (!hasSavedDraft || isSentRef.current) {
          return;
        }

        queryClient.invalidateQueries({ queryKey: MAIL_MESSAGE_IDS_QUERY_KEY });
        queryClient.invalidateQueries({ queryKey: MAIL_MESSAGES_QUERY_KEY });
      };

      saveQueueRef.current = saveQueueRef.current.then(refreshMailList);
    },
    [enqueueSave, queryClient],
  );

  return { quote, sendDraft };
};
